import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

/** True inside the Capacitor iOS and Android shells; false in any browser. */
export const isNative = Capacitor.isNativePlatform();

/**
 * Android uses the camera plugin (its gallery option is the permission-free Android photo picker). iOS uses the
 * WebView's file input instead: it opens Apple's photo picker with no photo-library permission, while the plugin
 * insists on library access first and rejects "Limited".
 */
export const photosViaCameraPlugin = Capacitor.getPlatform() === 'android';

const APP_ORIGIN = 'https://mylineage.app';

/** Light impact on selection and successful save (PRD 12.2). No-op on web. */
export function haptic() {
  if (isNative) void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}

/** Native share sheet on the apps, Web Share API or clipboard in a browser. Returns true if shared or copied. */
export async function shareLink(opts: { title: string; text: string; url: string }): Promise<'shared' | 'copied' | 'cancelled'> {
  try {
    if (isNative) {
      await Share.share({ ...opts, dialogTitle: opts.title });
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share(opts);
      return 'shared';
    }
  } catch {
    return 'cancelled';
  }
  await navigator.clipboard?.writeText(`${opts.text}\n${opts.url}`);
  return 'copied';
}

/** Camera or library prompt (Android). Returns a JPEG File, or null if cancelled. */
export async function pickNativePhoto(): Promise<File | null> {
  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Prompt,
      resultType: CameraResultType.Uri,
      correctOrientation: true,
      quality: 90,
    });
    if (!photo.webPath) return null;
    const blob = await (await fetch(photo.webPath)).blob();
    return new File([blob], `photo.${photo.format || 'jpg'}`, { type: blob.type || 'image/jpeg' });
  } catch {
    return null; // user cancelled or denied permission
  }
}

/** Last good API response, written to the app's data folder so screens still render offline (mobile only). */
export async function saveCache(path: string, value: unknown) {
  if (!isNative) return;
  try {
    await Filesystem.writeFile({ path, data: JSON.stringify(value), directory: Directory.Data, encoding: Encoding.UTF8, recursive: true });
  } catch {
    /* cache is best effort */
  }
}

export async function loadCache<T>(path: string): Promise<T | null> {
  if (!isNative) return null;
  try {
    const r = await Filesystem.readFile({ path, directory: Directory.Data, encoding: Encoding.UTF8 });
    return JSON.parse(typeof r.data === 'string' ? r.data : await r.data.text()) as T;
  } catch {
    return null;
  }
}

/** Removes every cached tree, tree list and thumbnail, so nothing stays on the device after sign-out. */
export async function clearCaches() {
  if (!isNative) return;
  for (const path of ['cache', 'trees', 'thumbs']) {
    await Filesystem.rmdir({ path, directory: Directory.Data, recursive: true }).catch(() => {});
  }
}

/** Cache a 256 px thumbnail as base64 so cards show offline. */
export async function saveThumb(photoId: string, blob: Blob) {
  if (!isNative) return;
  try {
    const data = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result).split(',')[1] ?? '');
      fr.onerror = () => rej(fr.error);
      fr.readAsDataURL(blob);
    });
    await Filesystem.writeFile({ path: `thumbs/${photoId}.jpg`, data, directory: Directory.Data, recursive: true });
  } catch {
    /* best effort */
  }
}

export async function loadThumb(photoId: string): Promise<string | null> {
  if (!isNative) return null;
  try {
    const r = await Filesystem.readFile({ path: `thumbs/${photoId}.jpg`, directory: Directory.Data });
    return `data:image/jpeg;base64,${typeof r.data === 'string' ? r.data : ''}`;
  } catch {
    return null;
  }
}

/** Turn a universal or app link into an in-app path, or null if it is not one of ours. */
export function pathFromLink(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.origin !== APP_ORIGIN) return null;
    return /^\/(i|t)\//.test(u.pathname) ? u.pathname + u.search : null;
  } catch {
    return null;
  }
}

/**
 * One-time native setup: status bar, splash, deep links and the Android back button
 * (closes modal, then sheet, then navigates back, then exits).
 */
export function initNative(navigate: (path: string) => void, goBack: () => void, canGoBack: () => boolean) {
  if (!isNative) return;
  void StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
  const syncBar = () => {
    const dark = document.documentElement.getAttribute('data-theme') === 'dark' || (!document.documentElement.hasAttribute('data-theme') && matchMedia('(prefers-color-scheme: dark)').matches);
    void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
  };
  syncBar();
  new MutationObserver(syncBar).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  void SplashScreen.hide().catch(() => {});

  void App.addListener('appUrlOpen', ({ url }) => {
    const p = pathFromLink(url);
    if (p) navigate(p);
  });
  void App.addListener('backButton', () => {
    // Topmost layer first: a modal opens over the profile sheet.
    const modal = document.querySelector<HTMLElement>('[role="dialog"] button[aria-label="Close"]');
    const sheet = document.querySelector<HTMLElement>('button[data-close-profile]');
    if (modal) modal.click();
    else if (sheet) sheet.click();
    else if (canGoBack()) goBack();
    else void App.exitApp();
  });
}
